import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Divider,
} from '@mui/material';
import {
  Block,
  CheckCircle,
  Email,
  Phone,
  CalendarToday,
  ContentCopy,
  QrCode2,
  Hotel,
  Event,
  Campaign,
  Handshake,
  DeleteOutline,
} from '@mui/icons-material';
import { Partnership, PartnershipType } from '@/services/partnershipsService';
import toast from 'react-hot-toast';

interface PartnershipCardProps {
  partnership: Partnership;
  onView: (partnership: Partnership) => void;
  onActivate?: (partnership: Partnership) => void;
  onSuspend?: (partnership: Partnership) => void;
  onDelete?: (partnership: Partnership) => void;
}

const PartnershipCard: React.FC<PartnershipCardProps> = ({
  partnership,
  onView,
  onActivate,
  onSuspend,
  onDelete,
}) => {
  // Type color and label mapping
  const getTypeInfo = (type: PartnershipType) => {
    switch (type) {
      case 'hotel':
        return { color: 'primary' as const, label: 'Hotel', icon: Hotel };
      case 'event_planner':
        return { color: 'secondary' as const, label: 'Event Planner', icon: Event };
      case 'promoter':
        return { color: 'info' as const, label: 'Promoter', icon: Campaign };
      default:
        return { color: 'default' as const, label: type, icon: Handshake };
    }
  };

  const typeInfo = getTypeInfo(partnership.partnershipType);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Copy referral code to clipboard
  const handleCopyReferralCode = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(partnership.referralCode);
    toast.success('Referral code copied to clipboard!');
  };

  return (
    <Card
      variant="outlined"
      onClick={() => onView(partnership)}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s',
        opacity: partnership.isActive ? 1 : 0.7,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <typeInfo.icon sx={{ fontSize: 20, color: `${typeInfo.color}.main` }} />
              <Typography variant="h6" component="div" noWrap>
                {partnership.companyName}
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
              <Chip
                label={typeInfo.label}
                color={typeInfo.color}
                size="small"
                sx={{ height: 22 }}
              />
              <Chip
                label={partnership.isActive ? 'Active' : 'Inactive'}
                color={partnership.isActive ? 'success' : 'default'}
                size="small"
                sx={{ height: 22 }}
              />
              {partnership.customCommissionRate && (
                <Chip
                  label={`Custom Rate: ${partnership.customCommissionRate}%`}
                  color="warning"
                  size="small"
                  sx={{ height: 22 }}
                />
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={0.5}>
            {!partnership.isActive && onActivate && (
              <Tooltip title="Reactivate">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onActivate(partnership);
                  }}
                  color="success"
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {partnership.isActive && onSuspend && (
              <Tooltip title="Suspend">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSuspend(partnership);
                  }}
                  color="warning"
                >
                  <Block fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(partnership);
                  }}
                  color="error"
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Referral Code */}
        <Box mb={2} p={1.5} bgcolor="primary.50" borderRadius={1} border="1px dashed" borderColor="primary.main">
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Referral Code
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight="bold" color="primary.main" fontFamily="monospace">
              {partnership.referralCode}
            </Typography>
            <Box display="flex" gap={0.5}>
              <Tooltip title="Copy">
                <IconButton size="small" onClick={handleCopyReferralCode} color="primary">
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="QR Code">
                <IconButton size="small" color="primary">
                  <QrCode2 fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Contact Information */}
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {partnership.email}
            </Typography>
          </Box>
          {partnership.phone && (
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {partnership.phone}
              </Typography>
            </Box>
          )}
          {partnership.contactPerson && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
              Contact: {partnership.contactPerson}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Activation Date */}
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Activated: {formatDate(partnership.activatedAt)}
          </Typography>
        </Box>

        <Box mt={1} p={1} bgcolor="action.hover" borderRadius={1} border="1px solid" borderColor="divider" minHeight={36}>
          <Typography variant="caption" color="text.secondary">
            {partnership.notes || '—'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PartnershipCard;
